import type {RawAxiosRequestHeaders} from "axios";
import * as Axios from 'axios';
import {useAuthStore} from '@/stores';
import {ensureCrmAccess} from '@/stores/refreshOrLogin';
import {CRM_RESOURCE} from '@/stores/resources';
import {config} from '@/env-config';

export class CrmService {

    public async queryOData<T = any>(
        pathAndQuery: string,
        options: RequestInit = {}
    ): Promise<T | null> {
        if (await ensureCrmAccess()) {
            return null;
        }

        const accessToken = useAuthStore().getAccessToken(CRM_RESOURCE);
        const path = pathAndQuery.replace(/^\//, '');

        try {
            const apiConfig: Axios.AxiosRequestConfig = {
                method: 'get',
                url: `${config.crmApiHost}/api/v1.0/odata/${path}`,
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    Accept: "application/json",
                    "Content-Type": "application/json",
                    ...(options.headers || {}),
                } as RawAxiosRequestHeaders
            }

            const res: Axios.AxiosResponse = await Axios.default(apiConfig);
            return res.data as T;
        } catch (e: any) {
            console.error(`CRM OData query failed: ${e.message}`);
            return null;
        }
    }

    public async getEntityFieldDefinitions(entityName: string) {

        const queryStr = `/EntityDefinitions(LogicalName='${entityName}')/Attributes?$select=LogicalName,AttributeType,SchemaName,DisplayName`

        const data = await this.queryOData(queryStr);
        // console.log(JSON.stringify(data, null, 4));

        return data.value;
    }

    public async getAccountByAccountNumber(accountNumber: string): Promise<any> {

        const queryStr = `/accounts?$filter=accountnumber eq '${accountNumber}'`
            // add this in to get the primary contact expanded.
            + `&$expand=primarycontactid($select=fullname,emailaddress1,telephone1)`
            // expands to get the wholesaler id/name from the wholesaler account
            + `,lon_PrimaryWholesalerAccount($select=_lon_wholesaler_value)`
            + `,lon_PrimaryWholesalerAccountOTC($select=_lon_wholesaler_value)`

        ;

        const headers = {
            Prefer: "odata.include-annotations=*"
        };

        // ** Organisation Tab

        // Main area
        // some field to note:
        // name,
        // key account: _lon_keyaccount_value, _lon_keyaccount_value@OData.Community.Display.V1.FormattedValue
        // operating group: _lon_operatinggrouprx_value, _lon_operatinggrouprx_value@OData.Community.Display.V1.FormattedValue
        // RTS OTC: lon_rtsotc
        // RTS RX: lon_rtsrx
        // Owner Group: _lon_ownergroup_value, _lon_ownergroup_value@OData.Community.Display.V1.FormattedValue
        // Intellipharm Customer ID: lon_intellipharmcustomerid
        // Nostra Id: lon_nostraid
        // Pharma Program ID: lon_pharmaprogramid
        // External Customer ID: lon_externalcustomerid
        // Allocation: lon_allocations

        // Address section:
        // Address 1: Street 1 : address1_line1
        // Address 1: Street 2 : address1_line2
        // Address 1: Street 3 : address1_line3
        // Suburb: address1_city
        // Address 1: State/Province: address1_stateorprovince
        // Address 1: ZIP/Postal Code: address1_postalcode
        // Address 1: Country/Region: address1_country


        // Contact Details
        // Primary Contact: _primarycontactid_value (and likely the name is "_primarycontactid_value@OData.Community.Display.V1.FormattedValue"
        // Phone: telephone1
        // Fax: fax
        // Email (RX/General EDM): emailaddress1
        // Email (OTC EDM): emailaddress2


        // Next bit:
        // Organisation Type: lon_organisationtype, lon_organisationtype@OData.Community.Display.V1.FormattedValue
        // Primary Wholesaler Account (RX): _lon_primarywholesaleraccount_value, _lon_primarywholesaleraccount_value@OData.Community.Display.V1.FormattedValue
        // Wholesaler (RX): lon_PrimaryWholesalerAccount._lon_wholesaler_value, lon_PrimaryWholesalerAccount._lon_wholesaler_value@OData.Community.Display.V1.FormattedValue
        // Primary Wholesaler Account (OTC): _lon_primarywholesaleraccountotc_value, _lon_primarywholesaleraccountotc_value@OData.Community.Display.V1.FormattedValue
        // Wholesaler (RX): lon_PrimaryWholesalerAccountOTC._lon_wholesaler_value, lon_PrimaryWholesalerAccountOTC._lon_wholesaler_value@OData.Community.Display.V1.FormattedValue
        // ABN: lon_abn
        // MPS Pharmacy: lon_mpspharmacy

        // ** Business Development Tab

        // LEVEL OF SUPPORT

        // Sales 1st Line Support ETH: lon_sales1stlinesupporteth, lon_sales1stlinesupporteth@OData.Community.Display.V1.FormattedValue
        // Sales 1st Line Support OTC: lon_sales1stlinesupportotc, lon_sales1stlinesupportotc@OData.Community.Display.V1.FormattedValue
        // BD Grade: lon_storegrade, lon_storegrade@OData.Community.Display.V1.FormattedValue
        // OTC Grade: lon_otcgrade, lon_otcgrade@OData.Community.Display.V1.FormattedValue
        // Pharmacy Type: lon_bdpharmacytype, lon_bdpharmacytype@OData.Community.Display.V1.FormattedValue
        // RX Size: lon_bdrxsize
        // OTC Size: lon_bdotcsize
        // Last Activity Date: lon_lastactivitydate, lon_lastactivitydate@OData.Community.Display.V1.FormattedValue
        // Open Active Priorities: lon_openactivepriorities, lon_openactivepriorities@OData.Community.Display.V1.FormattedValue
        // Last updated: lon_openactivepriorities_date, lon_openactivepriorities_date@OData.Community.Display.V1.FormattedValue

        // Sales 2nd Line Support ETH: lon_sales2ndlinesupporteth, lon_sales2ndlinesupporteth@OData.Community.Display.V1.FormattedValue
        // Sales 2nd Line Support OTC: lon_sales2ndlinesupportotc, lon_sales2ndlinesupportotc@OData.Community.Display.V1.FormattedValue
        // New Business Target: lon_newbusinesstarget, lon_newbusinesstarget@OData.Community.Display.V1.FormattedValue
        // Geo Profile: lon_bdgeoprofile, lon_bdgeoprofile@OData.Community.Display.V1.FormattedValue
        // Last Rep Visit RX: lon_lastrepvisitrx, lon_lastrepvisitrx@OData.Community.Display.V1.FormattedValue
        // Last Rep Visit OTC: lon_lastrepvisitotc, lon_lastrepvisitotc@OData.Community.Display.V1.FormattedValue
        // IPA Strategic Range Type: bit_ipastrategicrangetype, bit_ipastrategicrangetype@OData.Community.Display.V1.FormattedValue?

        // CONTRACT (Complete +marked fields to create new Opportunity)

        // Under Contract Rx: lon_bdundercontractrx, lon_bdundercontractrx@OData.Community.Display.V1.FormattedValue
        // Contract Start Date RX: lon_contractstartdaterx, lon_contractstartdaterx@OData.Community.Display.V1.FormattedValue
        // Contract Renewal Date Rx: lon_bdcontractrenewaldaterx, lon_bdcontractrenewaldaterx@OData.Community.Display.V1.FormattedValue
        // Current RX Pricing: lon_bdcurrentrxpricing, ?
        // Date Won: lon_bddatewon, lon_bddatewon@OData.Community.Display.V1.FormattedValue?

        // Under Contract OTC: lon_bdundercontractotc, lon_bdundercontractotc@OData.Community.Display.V1.FormattedValue
        // Contract Start Date OTC: lon_contractstartdateotc, lon_contractstartdateotc@OData.Community.Display.V1.FormattedValue
        // Contract Renewal Date OTC: lon_bdcontractrenewaldateotc, lon_bdcontractrenewaldateotc@OData.Community.Display.V1.FormattedValue
        // Current OTC Pricing: lon_bdcurrentotcpricing, ?
        // Date Lost: lon_bddatelost, lon_bddatelost@OData.Community.Display.V1.FormattedValue?

        // BUSINESS DEVELOPMENT - PAL

        // PAL Membership level: lon_bdpalmembershiplevel, ?
        // PAL Catalogue Supporter: lon_bdpalcataloguesupporter, ?
        // PAL Ticketing Program Member: lon_bdpalticketingprogrammember, ?
        // PAL Annual Fee: lon_bdpalannualfee, ?
        // Sigma Annual Fee: lon_bdsigmaannualfee, ?

        // Bonus Stock

        // Free Stock: lon_bdfreestock, ?
        // Drop 1: lon_bddrop1, ?
        // Loyalty: lon_bdloyalty, ?
        // Substitution: lon_bdsubstitution, ?
        // Free Stock Standard Exclusion: lon_bdfreestockstandardexclusion, ?
        // Drop 2: lon_bddrop1, ?
        // No Of Weeks Per Drop: lon_noofweeksperdrop, ?

        // Price Group Setup (table) TODO


        // ** RX Profiling Tab

        // Sales 1st Line Support OTC: lon_sales1stlinesupportotc, lon_sales1stlinesupportotc@OData.Community.Display.V1.FormattedValue
        // Sales 1st Line Support ETH: lon_sales1stlinesupporteth, lon_sales1stlinesupporteth@OData.Community.Display.V1.FormattedValue
        // RX Segmentation: lon_bdrxsize
        // APO-Access Member: lon_apoaccessmember, ?
        // NPD Rx: lon_npdrx
        // RTS RX: lon_rtsrx
        // Buy IT Right: lon_buyitright

        // Sales 2nd Line Support OTC: lon_sales2ndlinesupportotc, lon_sales2ndlinesupportotc@OData.Community.Display.V1.FormattedValue
        // Sales 2nd Line Support ETH: lon_sales2ndlinesupporteth, lon_sales2ndlinesupporteth@OData.Community.Display.V1.FormattedValue
        // OTC Grade: lon_otcgrade, ?
        // APO-Access Membership Level: lon_apoaccessmembershiplevel, ?
        // Rx Coverage Preference: lon_rxcoveragepreference, ?
        // Last Rep Visit RX: lon_lastrepvisitrx, ?

        // DAA - Consumables: lon_daaconsumables, lon_daaconsumables@OData.Community.Display.V1.FormattedValue
        // DAA - Automation: lon_daaautomation, lon_daaautomation@OData.Community.Display.V1.FormattedValue
        // DAA - Software: lon_daasoftware, lon_daasoftware@OData.Community.Display.V1.FormattedValue
        // Dispense System:lon_dispensesystem, lon_dispensesystem@OData.Community.Display.V1.FormattedValue
        // Dispense Robot Model: lon_dispenserobotmodel, lon_dispenserobotmodel@OData.Community.Display.V1.FormattedValue
        // DAA - Packing Frequency: lon_daapackingfrequency, lon_daapackingfrequency@OData.Community.Display.V1.FormattedValue
        // DAA - Community Packs/week lon_daacommunitypacksweek, lon_daacommunitypacksweek@OData.Community.Display.V1.FormattedValue:
        // DAA - Aged Care Packs/week: lon_daaagedcarepacksweek, @OData.Community.Display.V1.FormattedValue
        // POS System: lon_possystem, lon_possystem@OData.Community.Display.V1.FormattedValue
        // Dispense Volume Grade: bit_dispensevolumegrade, bit_dispensevolumegrade@OData.Community.Display.V1.FormattedValue

        // --- Specialty Brand Discounts Loaded ---
        // Leo-Advantan/Supriad: lon_leoadvantansupriad, lon_leoadvantansupriad@OData.Community.Display.V1.FormattedValue
        // Servier - Ardix Gliclazide: lon_servierardixgliclazide, lon_servierardixgliclazide@OData.Community.Display.V1.FormattedValue
        // Servier - Domion: lon_servierdomion, lon_servierdomion@OData.Community.Display.V1.FormattedValue
        // MSD - Sitagliptin: lon_msdsitagliptin, @OData.Community.Display.V1.FormattedValue
        // Pain Portfolio: lon_painportfolio, lon_painportfolio@OData.Community.Display.V1.FormattedValue
        // Respiratory Portfolio: lon_respiratorypotfolio, lon_respiratorypotfolio@OData.Community.Display.V1.FormattedValue
        // Takeda - Mesalazine 1.2: lon_taledamesalazine12, lon_taledamesalazine12@OData.Community.Display.V1.FormattedValue

        // Programs Table (TODO)

        // -- Biologics --
        // Etanercept Monthly Volume: lon_etanerceptmonthlyvolume, lon_etanerceptmonthlyvolume@OData.Community.Display.V1.FormattedValue
        // Etanercept ARX Share: lon_etanerceptarxshare, lon_etanerceptarxshare@OData.Community.Display.V1.FormattedValue
        // Biologics Status: lon_biologicsstatus, lon_biologicsstatus@OData.Community.Display.V1.FormattedValue
        // Adalimumab Monthly Volume: lon_adalimumabmonthlyvolume, lon_adalimumabmonthlyvolume@OData.Community.Display.V1.FormattedValue
        // Adalimumab ARX Share: lon_adalimumabarxshare, lon_adalimumabarxshare@OData.Community.Display.V1.FormattedValue
        // Adalimumab Preferred Biosimilar: lon_adalimumabpreferredbiosimilar, lon_adalimumabpreferredbiosimilar@OData.Community.Display.V1.FormattedValue

        // --- Wellness Clinics (complete this section to activate new clinic opportunity booking) ---
        // Primary Service 1: lon_clinicprimaryservice1, lon_clinicprimaryservice1@OData.Community.Display.V1.FormattedValue
        // Primary Service 2: lon_clinicprimaryservice2, lon_clinicprimaryservice2@OData.Community.Display.V1.FormattedValue
        // Primary Service 3: lon_clinicprimaryservice3, lon_clinicprimaryservice3@OData.Community.Display.V1.FormattedValue
        // Clinic History: lon_clinichistory
        // Clinic Per Annum Allowance: lon_clinicperannumallowance, lon_clinicperannumallowance@OData.Community.Display.V1.FormattedValue
        // Contract Professional Service: lon_cliniccontractprofessionalservice

        // Consulting Room: lon_clinicconsultingroom, lon_clinicconsultingroom@OData.Community.Display.V1.FormattedValue
        // Consulting Room Internet: lon_clinicconsultingroominternet, lon_clinicconsultingroominternet@OData.Community.Display.V1.FormattedValue
        // Clinic Wait Space: lon_clinicwaitspace, lon_clinicwaitspace@OData.Community.Display.V1.FormattedValue
        // Toilet Facilities: lon_clinictoiletfacilities, lon_clinictoiletfacilities@OData.Community.Display.V1.FormattedValue
        // Onsite Access Options: lon_cliniconsiteaccessoptions, lon_cliniconsiteaccessoptions@OData.Community.Display.V1.FormattedValue
        // Vaccination Service Type: lon_vaccinationservicetype, lon_vaccinationservicetype@OData.Community.Display.V1.FormattedValue
        // No. Vaccination Accredited Pharmacists: lon_novaccinationaccreditedpharmacists
        // Vaccination Weekly Access Hours: lon_vaccinationweeklyaccesshours, lon_vaccinationweeklyaccesshours@OData.Community.Display.V1.FormattedValue


        // ** OTC Profiling Tab

        // BD


        // ** Note & Activities tab


        // ** RX Cycle Priorities (TODO)
        // ** OTC Cycle Priorities (TODO)




        const data = await this.queryOData(queryStr, {headers});
        // console.log(JSON.stringify(data, null, 4));
        return data?.value[0] || null;
    }

    public async getContactsForAccount(accountId: string): Promise<any> {
        const queryStr = `/contacts?` +
            `$select=fullname,lon_jobtitle,telephone1,mobilephone,emailaddress1` +
            `&$filter=statecode eq 0 and _parentcustomerid_value eq '${accountId}'`;

        // Field on tjhe table on the contacts tab
        // Job Title: lon_jobtitle
        // Name: fullname
        // Business Phone: telephone1
        // Mobile Phone: mobilephone
        // Email:emailaddress1

        // SUBSCRIPTION MANAGEMENT (from account)
        // Generics Newsletter : lon_subsgenericsnewsletter, lon_subsgenericsnewsletter@OData.Community.Display.V1.FormattedValue (true/false, but formatted as yes/no)
        // Generics Product Updates: lon_subsgenericsproductupdates,lon_subsgenericsproductupdates@OData.Community.Display.V1.FormattedValue (same ^^)
        // Nutri Future Steps: lon_subsnutrifuturesteps, lon_subsnutrifuturesteps@OData.Community.Display.V1.FormattedValue
        // OTC Newsletter: lon_subsotcnewsletter,lon_subsotcnewsletter@OData.Community.Display.V1.FormattedValue

        // Website: websiteurl (from account)

        // CONTACT PREFERENCES (from account)
        // Contact Method: preferredcontactmethodcode, preferredcontactmethodcode@OData.Community.Display.V1.FormattedValue
        // Email: donotemail
        // Bulk Email: donotbulkemail
        // Phone: donotphone
        // Fax: donotfax
        // Mail: donotpostalmail


        const data = await this.queryOData(queryStr);
        // console.log(JSON.stringify(data, null, 4));
        return data.value;

    }

    public async getRepsForAccount(accountId: string): Promise<any> {

        const queryStr =
            `/lon_salesrepassignments?` +
            `$select=_lon_salesrep_value,lon_salesrepassignmentid, _lon_division_value` +
            `&$filter=statecode eq 0 and _lon_organisation_value eq ${accountId}` +
            `&$orderby=_lon_division_value asc` +
            `&$top=50` +
            `&$expand=` +
            `lon_SalesRep($select=internalemailaddress,fullname,isdisabled)`;

        const fetchXml = `
            <fetch version="1.0" output-format="xml-platform" mapping="logical"
                   distinct="false" returntotalrecordcount="true"
                   page="1" count="50" no-lock="false">
            
              <entity name="lon_salesrepassignment">
            
                <attribute name="lon_name" />
                <attribute name="statecode" />
                <attribute name="lon_salesrep" />
                <attribute name="lon_organisation" />
                <attribute name="lon_division" />
                <attribute name="lon_salesrepassignmentid" />
            
                <order attribute="lon_division" descending="false" />
            
                <filter type="and">
                  <condition attribute="statecode" operator="eq" value="0" />
                </filter>
                <link-entity name="systemuser"
                             from="systemuserid"
                             to="lon_salesrep"
                             alias="aa">
                  <filter type="and">
                    <condition attribute="isdisabled" operator="eq" value="0" />
                  </filter>
                </link-entity>
                <link-entity name="account"
                             from="accountid"
                             to="lon_organisation"
                             alias="bb">
                  <filter type="and">
                    <condition attribute="accountid"
                               operator="eq"
                               uitype="account"
                               value="${accountId}" />
                  </filter>
                </link-entity>
              </entity>
            </fetch>        
        `

        const queryStr2 = `/annotations?fetchXml=${encodeURIComponent(fetchXml)}`;

        // for the sales rep table
        // name: _lon_salesrep_value@OData.Community.Display.V1.FormattedValue
        // Division: _lon_division_value, _lon_division_value@OData.Community.Display.V1.FormattedValue

        const headers = {
            Prefer: "odata.include-annotations=*"
        };

        const data = await this.queryOData(queryStr2, {headers});
        // console.log(JSON.stringify(data, null, 4));
        return data.value;
    }

    public async getNotesForAccount(accountId: string): Promise<any> {

        const queryStr =
            `/annotations?` +
            // `$select=annotationid,subject,notetext,filename,filesize,isdocument,createdby,createdon,modifiedby,modifiedon,mimetype,isautonomouslycreated,_objectid_value` +
            `$filter=_objectid_value eq ${accountId}` +
            `&$orderby=modifiedon desc,annotationid asc` +
            `&$top=10` +
            `&$expand=modifiedby($select=systemuserid,fullname,entityimage_url)`;

        const fetchXml = `
            <fetch version="1.0" output-format="xml-platform" mapping="logical"
                   distinct="true" returntotalrecordcount="false"
                   page="1" count="10" no-lock="false">
            
              <entity name="annotation">
            
                <attribute name="annotationid" />
                <attribute name="subject" />
                <attribute name="notetext" />
                <attribute name="filename" />
                <attribute name="filesize" />
                <attribute name="isdocument" />
                <attribute name="createdby" />
                <attribute name="createdon" />
                <attribute name="modifiedby" />
                <attribute name="modifiedon" />
                <attribute name="mimetype" />
                <attribute name="isautonomouslycreated" />
            
                <order attribute="modifiedon" descending="true" />
                <order attribute="annotationid" descending="false" />
            
                <link-entity name="systemuser"
                             from="systemuserid"
                             to="modifiedby"
                             alias="systemuser"
                             link-type="outer">
            
                  <attribute name="entityimage_url" />
                  <attribute name="systemuserid" />
                  <attribute name="fullname" />
            
                </link-entity>
            
                <filter type="and">
                  <filter type="and">
                    <condition attribute="objectid"
                               operator="eq"
                               value="${accountId}" />
                  </filter>
                </filter>
            
              </entity>
            </fetch>        
        `

        const queryStr2 = `/annotations?fetchXml=${encodeURIComponent(fetchXml)}`;

        // dev note: The _ownerid_value could be a team or a user.  So, you need to fetch the annotations first, then
        // fetch each owner

        const headers = {
            Prefer: "odata.include-annotations=*"
        };

        const data = await this.queryOData(queryStr2, {headers});
        // console.log(JSON.stringify(data, null, 4));
        return data.value;
    }

    public async getActivitiesForAccount(accountId: string): Promise<any> {

        const fetchXml = `
            <fetch version="1.0" output-format="xml-platform" mapping="logical"
               distinct="false" returntotalrecordcount="true"
               page="1" count="50" no-lock="false">
              <entity name="activitypointer">
                <attribute name="subject" />
                <attribute name="activitytypecode" />
                <attribute name="scheduledstart" />
                <attribute name="ownerid" />
                <attribute name="statecode" />
                <attribute name="activityid" />
                <order attribute="scheduledstart" descending="true" />
                <filter type="and">
                  <condition attribute="activitytypecode" operator="ne" value="10142" />
                </filter>
                <filter type="and">
                  <condition attribute="activitytypecode" operator="ne" value="4220" />
                </filter>
                <link-entity name="account"
                             from="accountid"
                             to="regardingobjectid"
                             alias="bb">
            
                  <filter type="and">
                    <condition attribute="accountid"
                               operator="eq"
                               uitype="account"
                               value="${accountId}" />
                  </filter>
                </link-entity>
              </entity>
            </fetch>`;

        const queryStr2 = `/activitypointers?fetchXml=${encodeURIComponent(fetchXml)}`;

        const queryStr =
            `/activitypointers?` +
            `$select=subject,activitytypecode,scheduledstart,_ownerid_value,statecode,activityid,_regardingobjectid_value` +
            `&$filter=_regardingobjectid_value eq ${accountId}` +
            // ` and activitytypecode ne '10142'` + // as per CRM UI
            // ` and activitytypecode ne '4220'` + // as per CRM UI
            ` and activitytypecode ne 'email'` +
            ` and activitytypecode ne 'phonecall'` +
            `&$orderby=scheduledstart desc` +
            `&$top=50` +
            `&$count=true`;


        const headers = {
            Prefer: "odata.include-annotations=*"
        };

        const data = await this.queryOData(queryStr2, {headers});
        // console.log(JSON.stringify(data, null, 4));

        // filter down to make sure the activities is related to the account
        // const results = data.value.filter( (r: any) =>
        //     r["_regardingobjectid_value@Microsoft.Dynamics.CRM.lookuplogicalname"] === "account"
        // );

        // return results;
        return data.value;
    }

    public async getSalesOrdersForAccount(accountId: string): Promise<any> {

        const queryStr =
            `/salesorders?` +
            // `$select=salesorderid,name,ordernumber,totalamount,statecode,statuscode,createdon,_customerid_value` +
            `$filter=_customerid_value eq ${accountId}` +
            // `&$expand=order_details($select=salesorderdetailid,productid,quantity,priceperunit,extendedamount,baseamount)`;
            `&$expand=order_details`;

        const fetchXml = `
            <fetch version="1.0" output-format="xml-platform" mapping="logical"
                   returntotalrecordcount="true"
                   page="1" count="50" no-lock="false">
            
              <entity name="salesorder">
            
                <attribute name="entityimage_url" />
                <attribute name="statecode" />
                <attribute name="name" />
                <attribute name="totalamount" />
                <attribute name="statuscode" />
                <attribute name="customerid" />
                <attribute name="createdon" />
                <attribute name="salesorderid" />
            
                <order attribute="createdon" descending="true" />
            
                <!-- Contact (optional via outer join) -->
                <link-entity name="contact"
                             from="contactid"
                             to="customerid"
                             alias="salesordercustomeridcontactcontactid"
                             link-type="outer"
                             visible="false">
            
                  <attribute name="emailaddress1" />
            
                </link-entity>
            
                <!-- Account filter (this is the real constraint) -->
                <link-entity name="account"
                             from="accountid"
                             to="customerid"
                             alias="bb">
            
                  <filter type="and">
                    <condition attribute="accountid"
                               operator="eq"
                               uitype="account"
                               value="${accountId}" />
                  </filter>
                </link-entity>
              </entity>
            </fetch>                
        `

        const queryStr2 = `/salesorders?fetchXml=${encodeURIComponent(fetchXml)}`;


        const headers = {
            Prefer: "odata.include-annotations=*"
        };

        const data = await this.queryOData(queryStr2, {headers});
        return data.value;
    }


    public async getSalesOrderDetailsForOder(orderId: string): Promise<any> {

        const fetchXml = `
            <fetch version="1.0" output-format="xml-platform" mapping="logical"
                   returntotalrecordcount="true"
                   page="1" count="50" no-lock="false">
            
              <entity name="salesorderdetail">
            
                <order attribute="salesorderdetailname" descending="false" />
            
                <attribute name="quantity" />
                <attribute name="lon_status" />
                <attribute name="lon_productonhold" />
                <attribute name="lon_pdecode" />
                <attribute name="salesorderdetailname" />
                <attribute name="lon_discountpercent" />
                <attribute name="lon_exporteddate" />
                <attribute name="lon_bonusreason" />
                <attribute name="salesorderdetailid" />
            
                <!-- Join to Sales Order (this is the filter) -->
                <link-entity name="salesorder"
                             from="salesorderid"
                             to="salesorderid"
                             alias="bb">
            
                  <filter type="and">
                    <condition attribute="salesorderid"
                               operator="eq"
                               uitype="salesorder"
                               value="${orderId}" />
                  </filter>
            
                </link-entity>
            
              </entity>
            </fetch>
        `

        const queryStr = `/salesorderdetails?fetchXml=${encodeURIComponent(fetchXml)}`;

        const headers = {
            Prefer: "odata.include-annotations=*"
        };

        const data = await this.queryOData(queryStr, {headers});
        return data.value;
    }


}
